import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton, DeleteButton } from "@refinedev/mui";
import { Chip, IconButton, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import { TableCell } from "@shared/components";
import { Partner, PartnerStatus } from "@modules/partners/types";
import { formatCurrency } from "@core/utils/masks";

const statusColor: Record<PartnerStatus, "warning" | "success" | "error"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

interface UsePartnersColumnsParams {
  translate: (key: string, options?: Record<string, unknown>) => string;
  refetch: () => void;
  onApprove: (id: number) => void;
  onReject: (partner: Partner) => void;
}

export const usePartnersColumns = ({
  translate,
  refetch,
  onApprove,
  onReject,
}: UsePartnersColumnsParams): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: translate("v1/partner_admin.fields.name"),
        minWidth: 200,
        headerAlign: "left",
        renderCell: ({ row, value }) => (
          <TableCell.Link to={`/partners/detail/${row.id}`} value={value} />
        ),
      },
      {
        field: "email",
        headerName: translate("v1/partner_admin.fields.email"),
        minWidth: 200,
        headerAlign: "left",
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "document",
        headerName: translate("v1/partner_admin.fields.document"),
        minWidth: 150,
        headerAlign: "left",
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "status",
        headerName: translate("v1/partner_admin.fields.status"),
        minWidth: 130,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Container align="center">
            <Chip
              size="small"
              color={statusColor[value as PartnerStatus] ?? "default"}
              label={translate(`partners.status.${value}`)}
            />
          </TableCell.Container>
        ),
      },
      {
        field: "total_mrr",
        headerName: translate("v1/partner_admin.fields.total_mrr"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text value={formatCurrency(value)} />
        ),
      },
      {
        field: "referrals_count",
        headerName: translate("v1/partner_admin.fields.referrals_count"),
        minWidth: 110,
        headerAlign: "center",
        renderCell: ({ value }) => <TableCell.Text value={value} />,
      },
      {
        field: "created_at",
        headerName: translate("v1/partner_admin.fields.created_at"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        display: "flex",
        headerAlign: "center",
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            {row.status === "pending" && (
              <>
                <Tooltip title={translate("partners.actions.approve")}>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onApprove(row.id)}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={translate("partners.actions.reject")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onReject(row as Partner)}
                  >
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <EditButton hideText recordItemId={row.id} resource="v1/partner_admin" />
            <DeleteButton
              hideText
              recordItemId={row.id}
              resource="v1/partner_admin"
              onSuccess={refetch}
            />
          </TableCell.Container>
        ),
      },
    ],
    [translate, refetch, onApprove, onReject]
  );
};
